import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { PointMm } from '~/domain/geometry/patientGeometry';
import {
    rectangleFrame,
    RegionGeometry,
} from '~/domain/intensity/intensityRegion';
import {
    SummaryStatistics,
    summaryStatistics,
} from '~/domain/statistics/summaryStatistics';
import type { Grid } from '~/engine/data/common/Grid';
import type { ImageObject } from './ImageObject';

/**
 * How far, in mm, a row of voxels may seem to lie off the region's slab and
 * still be scanned. Rows are bounded in double precision while the voxel test
 * that decides runs in single precision; the slack keeps the bound from ever
 * dropping a voxel the test would take.
 */
const ROW_SLACK_MM = 0.01;

/**
 * A region as the voxel scan tests it, with the slab it covers `halfDepthMm`
 * either side of its plane along the unit `normal`.
 */
interface RegionTest {
    readonly centerMm: PointMm;
    readonly normal: vec3;
    readonly halfDepthMm: number;
    /**
     * Whether a voxel centre at `offset` from the centre, `alongNormal` off
     * the plane, lies inside the outline.
     */
    readonly inPlane: (offset: vec3, alongNormal: number) => boolean;
    /** Half the extent of the slab-thick region along the linear form `row`. */
    readonly halfExtentAlong: (row: vec3) => number;
    /**
     * Whether the region is no wider than a voxel along any of its sides, so
     * that enclosing no voxel centre means it lies within about one voxel.
     */
    readonly withinAVoxel: boolean;
}

/**
 * The region `region` reads on `grid`, or null when it has no extent. It is
 * one voxel thick along its normal, the voxel size coming from the image, not
 * from the view showing it.
 */
function regionTest(
    region: RegionGeometry,
    normal: vec3,
    grid: Grid
): RegionTest | null {
    const halfDepthMm = grid.voxelExtentAlong(normal) / 2;
    const slab = (row: vec3) => halfDepthMm * Math.abs(vec3.dot(row, normal));
    if (region.shape === 'circle') {
        const { centerMm, radiusMm } = region;
        // Negated so a NaN radius is refused too.
        if (!(radiusMm > 0)) {
            return null;
        }
        const radiusSq = radiusMm * radiusMm;
        return {
            centerMm,
            normal,
            halfDepthMm,
            // Narrower than a voxel along every axis, whichever way it turns.
            withinAVoxel:
                2 * radiusMm < Math.min(grid.resX, grid.resY, grid.resZ),
            inPlane: (offset, alongNormal) =>
                vec3.squaredLength(offset) - alongNormal * alongNormal <=
                radiusSq,
            halfExtentAlong: (row) => {
                const along = vec3.dot(row, normal);
                const inPlaneSq = vec3.squaredLength(row) - along * along;
                return radiusMm * Math.sqrt(Math.max(0, inPlaneSq)) + slab(row);
            },
        };
    }
    const { centerMm, u, v, widthMm, heightMm } = rectangleFrame(
        region.cornersMm
    );
    if (!(widthMm > 0) || !(heightMm > 0)) {
        return null;
    }
    const halfWidth = widthMm / 2;
    const halfHeight = heightMm / 2;
    return {
        centerMm,
        normal,
        halfDepthMm,
        withinAVoxel:
            widthMm < grid.voxelExtentAlong(u) &&
            heightMm < grid.voxelExtentAlong(v),
        inPlane: (offset) =>
            Math.abs(vec3.dot(offset, u)) <= halfWidth &&
            Math.abs(vec3.dot(offset, v)) <= halfHeight,
        halfExtentAlong: (row) =>
            halfWidth * Math.abs(vec3.dot(row, u)) +
            halfHeight * Math.abs(vec3.dot(row, v)) +
            slab(row),
    };
}

/**
 * The value of every voxel inside the region, each once, in a fixed order
 * (z, then y, then x) so the result never varies between runs. Only the
 * voxels of the region's bounding box are visited, and within each row only
 * those the slab reaches, so the scan grows with the region's area, however
 * the plane lies in the grid. A voxel the image has no value for reads as
 * NaN, which {@link summaryStatistics} leaves out.
 */
function enclosedVoxelValues(image: ImageObject, test: RegionTest): number[] {
    const { grid } = image;
    const pixels = image.parent.floatPixelData;
    const toWorld = grid.voxelToWorld;
    const toVoxel = grid.worldToVoxel;
    const size = grid.getSize();
    const { centerMm, normal, halfDepthMm } = test;

    // Voxel centres sit at whole index plus one half; the box is padded by a
    // voxel so single-precision rounding can never put a centre outside it.
    const centre = grid.toVoxelSpace(centerMm);
    const lo = [0, 0, 0];
    const hi = [0, 0, 0];
    const row = vec3.create();
    for (let axis = 0; axis < 3; axis++) {
        vec3.set(row, toVoxel[axis], toVoxel[4 + axis], toVoxel[8 + axis]);
        const half = test.halfExtentAlong(row);
        const at = centre[axis] - 0.5;
        lo[axis] = Math.max(0, Math.ceil(at - half) - 1);
        hi[axis] = Math.min(size[axis] - 1, Math.floor(at + half) + 1);
    }

    // A voxel centre's distance off the plane is linear in its index:
    // perX per step along x, plus what its row and slice contribute.
    const perStep = (column: number) =>
        normal[0] * toWorld[column] +
        normal[1] * toWorld[column + 1] +
        normal[2] * toWorld[column + 2];
    const perX = perStep(0);
    const perY = perStep(4);
    const perZ = perStep(8);
    const origin =
        normal[0] * (toWorld[12] - centerMm[0]) +
        normal[1] * (toWorld[13] - centerMm[1]) +
        normal[2] * (toWorld[14] - centerMm[2]);
    const reach = halfDepthMm + ROW_SLACK_MM;

    const values: number[] = [];
    const voxel = vec3.create();
    const world = vec3.create();
    const offset = vec3.create();
    for (let z = lo[2]; z <= hi[2]; z++) {
        for (let y = lo[1]; y <= hi[1]; y++) {
            const rowOff = origin + perY * (y + 0.5) + perZ * (z + 0.5);
            let first = lo[0];
            let last = hi[0];
            if (perX !== 0) {
                const a = (-reach - rowOff) / perX - 0.5;
                const b = (reach - rowOff) / perX - 0.5;
                first = Math.max(first, Math.ceil(Math.min(a, b)));
                last = Math.min(last, Math.floor(Math.max(a, b)));
            } else if (Math.abs(rowOff) > reach) {
                continue;
            }
            for (let x = first; x <= last; x++) {
                // The exact test, on the single-precision voxel centre; the
                // row bounds above only narrow the scan.
                vec3.set(voxel, x + 0.5, y + 0.5, z + 0.5);
                vec3.transformMat4(world, voxel, toWorld);
                vec3.subtract(offset, world, centerMm);
                const alongNormal = vec3.dot(offset, normal);
                if (
                    Math.abs(alongNormal) > halfDepthMm ||
                    !test.inPlane(offset, alongNormal)
                ) {
                    continue;
                }
                values.push(
                    pixels[(z * size[1] + y) * size[0] + x] ?? Number.NaN
                );
            }
        }
    }
    return values;
}

/**
 * Intensity statistics (min, max, mean and SD) of the voxels inside `region`,
 * which lies in the plane with normal `normalDirection` (SR-WOR-H-17-5).
 *
 * A voxel is inside when its centre is, and each one is read once. The
 * figures therefore depend on the region and the image only: turning the view
 * does not change them, and a region tilted against the grid reads the voxels
 * it actually covers.
 *
 * Each voxel is read from the voxel the cursor readout finds at its centre,
 * so the two always agree.
 *
 * @returns null when the region or the normal is degenerate, when no voxel
 *   under the region has a finite value, or when a region wider than a voxel
 *   holds no voxel centre.
 * @throws when the image is still loading. A slice that has not arrived reads
 *   as no voxel, so figures over the slices present would look like the
 *   region's. Callers refuse the region before reading (SR-WOR-H-37-3).
 */
export function regionIntensityStatistics(
    image: ImageObject,
    region: RegionGeometry,
    normalDirection: ReadonlyVec3
): SummaryStatistics | null {
    if (!image.parent.isComplete) {
        throw new Error(
            'regionIntensityStatistics: the image is not whole; refuse the region before reading it'
        );
    }
    // Negated so a NaN normal is refused too.
    if (!(vec3.length(normalDirection) > 0)) {
        return null;
    }
    const normal = vec3.normalize(vec3.create(), normalDirection);
    const test = regionTest(region, normal, image.grid);
    if (!test) {
        return null;
    }
    const enclosed = summaryStatistics(enclosedVoxelValues(image, test));
    if (enclosed || !test.withinAVoxel) {
        return enclosed;
    }
    // A region smaller than a voxel encloses no voxel centre. The voxel it
    // sits inside is the only honest answer, and reporting it keeps a tiny
    // region from reading as "no data" over perfectly good image. A strip
    // longer than a voxel that still holds no centre gets no figures: one
    // voxel's value would pass for the whole strip's.
    const atCentre = image.getImageObjectValueByCoordinate(test.centerMm);
    return atCentre === null || !Number.isFinite(atCentre)
        ? null
        : summaryStatistics([atCentre]);
}
